import React from 'react';
import {flexRender} from '@tanstack/react-table';
import classnames from 'classnames';

import Text from '../../../atoms/Text/Text.react';
import './data-table.scss';

import type {DataTableCellProps} from './DataTable.types';

/**
 * Renders a single `<td>` for DataTable.
 *
 * Column metadata (`columnDef.meta`) controls rendering:
 * - `meta.align` — 'left' | 'center' | 'right'
 * - `meta.primary` — boolean, highlights the cell
 * - `meta.contentType` — 'text' (default, wraps in `<Text>`) or 'graphic' (renders raw)
 * - `meta.isActionMenu` — boolean, applies action-menu cell styling
 * - `meta.noClamp` — boolean, disables text clamping
 * - `meta.subValue` — function `(row) => string | undefined`, renders secondary text
 * - `meta.className` — string, additional CSS class(es) applied to the `<td>` for custom styling
 *
 */
function DataTableCell({cell}: DataTableCellProps) {
  const meta = (cell.column.columnDef.meta ?? {}) as Record<string, any>;
  const align = meta.align;
  const primary = meta.primary;
  const contentType = meta.contentType ?? 'text';
  const isActionMenu = meta.isActionMenu;
  const noClamp = meta.noClamp;
  const shouldClamp = contentType === 'text' && !noClamp;
  const cellClassName = meta.className;
  const subValueFn = meta.subValue;
  const subValue = typeof subValueFn === 'function' ? subValueFn(cell.row.original) : undefined;

  const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());

  const minSize = cell.column.columnDef.minSize;

  return (
    <td
      className={classnames('data-table__cell', cellClassName, {
        [`data-table__cell--${align}`]: align,
        'data-table__cell--primary': primary,
        'data-table__action-menu-cell': isActionMenu,
        'data-table__cell--clamp': shouldClamp
      })}
      style={minSize ? {minWidth: minSize} : undefined}
      {...(isActionMenu ? {onClick: (e: React.MouseEvent) => e.stopPropagation()} : {})}
    >
      {contentType === 'text' ? (
        <Text className='data-table__cell-text' size='s'>
          {rendered}
        </Text>
      ) : (
        rendered
      )}
      {subValue && (
        <Text as='div' className='data-table__sub-value' color='tertiary' size='xs'>
          {subValue}
        </Text>
      )}
    </td>
  );
}

export default DataTableCell;
